import React, { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { Award, Briefcase, Zap, ShieldCheck } from "lucide-react";

const CountUp = ({ end, duration = 2, suffix = "" }) => {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, margin: "-80px" });
    const [count, setCount] = useState(0);

    useEffect(() => {
        if (!inView) return;
        let startTimestamp = null;
        const step = (timestamp) => {
            if (!startTimestamp) startTimestamp = timestamp;
            const progress = Math.min((timestamp - startTimestamp) / (duration * 1000), 1);
            const easeProgress = 1 - Math.pow(1 - progress, 3);
            setCount(parseFloat((easeProgress * end).toFixed(end % 1 === 0 ? 0 : 1)));
            if (progress < 1) window.requestAnimationFrame(step);
        };
        window.requestAnimationFrame(step);
    }, [inView, end, duration]);

    return (
        <span ref={ref} className="font-extrabold text-5xl sm:text-6xl text-white bg-clip-text">
            {count}
            <span className="text-[#2F8F6F]">{suffix}</span>
        </span>
    );
};

const ImpactMetrics = () => {
    const metrics = [
        {
            icon: <Briefcase className="w-8 h-8 text-[#2F8F6F]" />,
            endValue: 5,
            suffix: "+",
            title: "Production Banking Systems",
            description: "Developed core frontends and critical feature expansions for top-tier Indonesian banks including Bank Saqu & Bank CIMB Niaga."
        },
        {
            icon: <Zap className="w-8 h-8 text-[#D7C7A1]" />,
            endValue: 20,
            suffix: "+",
            title: "Delivered Web & Mobile Products",
            description: "Built commercial enterprise applications, branch delivery management tools, and marketplaces from conception to play store deployment."
        },
        {
            icon: <ShieldCheck className="w-8 h-8 text-[#2F8F6F]" />,
            endValue: 99.9,
            suffix: "%",
            title: "Critical System Uptime",
            description: "Engineered performant, highly-optimized React codebases maintaining stable, crash-free operational runtimes."
        },
        {
            icon: <Award className="w-8 h-8 text-[#D7C7A1]" />,
            endValue: 100,
            suffix: "+",
            title: "Graphic & Brand Designs",
            description: "Curated aesthetic, visual identities, typography collections, and marketing materials for diverse commercial portfolios."
        }
    ];

    const containerVariants = {
        hidden: {},
        visible: { transition: { staggerChildren: 0.15 } }
    };

    const cardVariants = {
        hidden: { opacity: 0, y: 40, scale: 0.95 },
        visible: {
            opacity: 1,
            y: 0,
            scale: 1,
            transition: { type: "spring", bounce: 0.15, duration: 0.8 }
        }
    };

    return (
        <section className="relative py-24 bg-[#0F0F0F] overflow-hidden ">
            <div className="absolute top-1/2 left-1/4 -translate-y-1/2 w-[400px] h-[400px] bg-[#0B3D2E]/8 rounded-full blur-3xl -z-10" />
            <div className="absolute top-1/2 right-1/4 -translate-y-1/2 w-[400px] h-[400px] bg-[#145A43]/6 rounded-full blur-3xl -z-10" />

            <div className="max-w-6xl mx-auto px-6 sm:px-10 lg:px-12">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.8, type: "spring", bounce: 0.1 }}
                    className="text-center mb-16"
                >
                    <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-white mb-4">
                        Key <span className="text-[#2F8F6F]">Impact</span> & Performance
                    </h2>
                    <p className="text-slate-400 text-lg max-w-2xl mx-auto">
                        Quantifying developer efficiency, product stability, and commercial success.
                    </p>
                </motion.div>

                <motion.div
                    variants={containerVariants}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: "-80px" }}
                    className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6"
                >
                    {metrics.map((metric, idx) => (
                        <motion.div
                            key={idx}
                            variants={cardVariants}
                            whileHover={{
                                y: -10,
                                scale: 1.03,
                                borderColor: "rgba(47, 143, 111, 0.4)",
                                boxShadow: "0 20px 40px rgba(47, 143, 111, 0.12)"
                            }}
                            className="flex flex-col h-full bg-[#0B3D2E]/15 backdrop-blur-md border border-[#145A43]/40 rounded-2xl p-6 sm:p-8 transition-all duration-300 group cursor-pointer"
                        >
                            <div className="mb-6 p-3 bg-[#0B3D2E]/40 rounded-xl w-fit group-hover:bg-[#145A43]/40 transition-colors duration-300">
                                {metric.icon}
                            </div>

                            <div className="mb-4">
                                <CountUp end={metric.endValue} suffix={metric.suffix} />
                            </div>

                            <h3 className="text-lg font-bold text-white mb-3 group-hover:text-[#2F8F6F] transition-colors duration-300">
                                {metric.title}
                            </h3>

                            <p className="text-gray-400 text-sm leading-relaxed mt-auto">
                                {metric.description}
                            </p>
                        </motion.div>
                    ))}
                </motion.div>
            </div>
        </section>
    );
};

export default ImpactMetrics;